/**
 * types.ts — Types publics + Zod schemas du module kot-print-jobs (lot T1).
 *
 * File d'impression KOT avec claim atomique. NON FISCAL : aucun hash,
 * hors chaîne NF525. Payload = articles/quantités uniquement (aucun PII).
 *
 * Tous les types sont dérivés STRICTEMENT de Database['public']['Tables'].
 *
 * Invariants :
 *   4.A.10 — tenant_id jamais dans les inputs ; injecté par DEFAULT/RLS serveur.
 *   4.A.15 — payload sans PII : articles/quantités/notes de préparation uniquement.
 *   4.B.4  — 0 any, types stricts.
 */

import { z } from 'zod';
import type { Database } from '@/integrations/supabase/types';

// ---------------------------------------------------------------------------
// Row / Insert / Update — source de vérité DB
// ---------------------------------------------------------------------------

export type KotPrintJobRow = Database['public']['Tables']['kot_print_jobs']['Row'];
export type KotPrintJobInsert = Database['public']['Tables']['kot_print_jobs']['Insert'];
export type KotPrintJobUpdate = Database['public']['Tables']['kot_print_jobs']['Update'];

export type KotPrintJobStatus = 'pending' | 'claimed' | 'printed' | 'failed';

export const kotPrintJobStatusSchema = z.enum(['pending', 'claimed', 'printed', 'failed']);

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

/**
 * Payload = lignes du groupe à imprimer (articles, quantités, notes prépa).
 * Structure JSONB libre, union passthrough identique à draft_orders.items
 * pour supporter tableau ou record.
 * Invariant 4.A.15 : jamais de PII (nom client, email, téléphone…).
 */
const kotPayloadSchema = z
  .union([z.array(z.unknown()), z.record(z.string(), z.unknown())])
  .default([]);

/**
 * Création d'un job d'impression KOT.
 * tenant_id, status, created_at sont posés côté serveur (DEFAULT / RLS) —
 * jamais dans l'input (invariant 4.A.10).
 *
 * Champs :
 *   kot_key          : '<draftOrderId|orderNumber>:<sent_seq>' — clé d'idempotence.
 *   printer_function : null = groupe défaut (legacy mono-fonction).
 *   payload          : lignes du groupe — JSONB passthrough, aucun PII.
 */
export const createKotPrintJobSchema = z.object({
  kot_key: z.string().min(1, 'kot_key ne peut pas être vide'),
  printer_function: z.string().min(1).nullable().optional(),
  payload: kotPayloadSchema,
});
export type CreateKotPrintJobInput = z.infer<typeof createKotPrintJobSchema>;

/**
 * Schema pour le batch d'insertion idempotente.
 * Au moins un job requis (batch vide = appel inutile).
 */
export const createKotPrintJobsBatchSchema = z
  .array(createKotPrintJobSchema)
  .min(1, 'Le batch doit contenir au moins un job');
export type CreateKotPrintJobsBatchInput = z.infer<typeof createKotPrintJobsBatchSchema>;

// ---------------------------------------------------------------------------
// Filtres list
// ---------------------------------------------------------------------------

export type ListPendingJobsFilters = {
  /** Identifiant du tenant (belt-and-suspenders en plus de la RLS). */
  readonly tenantId: string;
};

// ---------------------------------------------------------------------------
// Alias export lisible
// ---------------------------------------------------------------------------

export type KotPrintJob = KotPrintJobRow;
